import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Bold as BoldIcon } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * Bold-only rich-text editor built on Tiptap 3 (ProseMirror).
 * Uses StarterKit with everything disabled except Document / Paragraph / Text /
 * Bold / History / HardBreak. document.execCommand is NOT used.
 * Server-side bleach still restricts to {b, strong, br, p}.
 */
export function BoldOnlyEditor({ value, onChange, disabled = false, testId = "custom-text-editor" }) {
  const { t } = useI18n();
  // Tiptap fires one onUpdate during its own initial mount transaction, even
  // though nothing was actually typed — skip that first call so callers
  // (e.g. a "did the user touch anything" dirty check) don't see a false edit.
  const hasMounted = useRef(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable everything but the marks/nodes we need.
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        horizontalRule: false,
        code: false,
        italic: false,
        strike: false,
        link: false,
      }),
    ],
    content: value || "",
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "min-h-[140px] px-3 py-2 text-sm leading-relaxed outline-none",
        "data-testid": `${testId}-area`,
        "data-placeholder": t("boldEditor.placeholder"),
      },
      // Strip all formatting on paste — text only.
      transformPastedHTML: () => "",
    },
    onUpdate: ({ editor: ed }) => {
      if (!hasMounted.current) return;
      const html = ed.getHTML();
      const empty = html.replace(/\s+/g, "") === "<p></p>";
      onChange(empty ? "" : html);
    },
    onCreate: () => {
      hasMounted.current = true;
    },
  });

  // Sync incoming `value` prop without disrupting the user's cursor / edits.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    const currentEmpty = current.replace(/\s+/g, "") === "<p></p>";
    const incomingEmpty =
      incoming.replace(/\s+/g, "") === "" ||
      incoming.replace(/\s+/g, "") === "<p></p>";
    if ((currentEmpty && incomingEmpty) || current === incoming) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const toggleBold = () => {
    if (!editor || disabled) return;
    editor.chain().focus().toggleBold().run();
  };

  // Tiptap's `useEditor` doesn't trigger React re-renders on selection / mark changes,
  // so we subscribe to the editor state slice we care about to keep the button in sync.
  const isBoldActive = useEditorState({
    editor,
    selector: (ctx) => (ctx.editor ? ctx.editor.isActive("bold") : false),
  });

  return (
    <div className="rounded-md border border-input bg-background" data-testid={testId}>
      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
        <Button
          type="button"
          variant={isBoldActive ? "secondary" : "ghost"}
          size="sm"
          onClick={toggleBold}
          disabled={disabled}
          data-testid={`${testId}-bold-btn`}
          data-active={isBoldActive}
          className="h-7 gap-1 px-2 font-mono text-[10px] uppercase tracking-widest"
          aria-label={t("boldEditor.bold")}
          aria-pressed={isBoldActive}
        >
          <BoldIcon className="h-3.5 w-3.5" />
          {t("boldEditor.bold")}
        </Button>
        <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {t("boldEditor.plainTextPrefix")}
          <b>{t("boldEditor.boldLower")}</b>
          {t("boldEditor.plainTextSuffix")}
        </span>
      </div>
      <EditorContent
        editor={editor}
        className="focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background rounded-b-md"
      />
    </div>
  );
}
